import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  approveBlock,
  liftBlock,
  rejectBlock,
  requestBlock,
} from '../api'
import type {
  ApproveBlockPayload,
  LiftBlockPayload,
  RejectBlockPayload,
  RequestBlockPayload,
} from '../types'

function invalidateBlocks(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: ['blocks'] })
}

export function useRequestBlock() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload: RequestBlockPayload) => requestBlock(payload),
    onSuccess: () => {
      toast.success('Block request submitted')
      invalidateBlocks(qc)
    },
    onError: (err) =>
      toast.error(err instanceof Error ? err.message : "Couldn't submit block request"),
  })
}

export function useApproveBlock() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload: ApproveBlockPayload) => approveBlock(payload),
    onSuccess: () => {
      toast.success('Block approved')
      invalidateBlocks(qc)
    },
    onError: (err) =>
      toast.error(err instanceof Error ? err.message : "Couldn't approve block"),
  })
}

export function useRejectBlock() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload: RejectBlockPayload) => rejectBlock(payload),
    onSuccess: () => {
      toast.success('Block rejected')
      invalidateBlocks(qc)
    },
    onError: (err) =>
      toast.error(err instanceof Error ? err.message : "Couldn't reject block"),
  })
}

export function useLiftBlock() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload: LiftBlockPayload) => liftBlock(payload),
    onSuccess: () => {
      toast.success('Block lifted')
      invalidateBlocks(qc)
    },
    onError: (err) =>
      toast.error(err instanceof Error ? err.message : "Couldn't lift block"),
  })
}
